import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    CardContent,
    CardHeader,
    CircularProgress,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import type { PageConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import { useCommonTranslation } from 'c-translation';
import { dataStatusLineItemsIcon } from 'c-main/icons';
import type {
    FilterableEntityTableProps,
    FilterableEntityTableRefAPI,
    FilterConfigSelect,
    RenderColumnDataProps,
} from 'c-pagination';
import { CustomFilterConfigType, FilterableEntityTable, generateColumn } from 'c-pagination';
import type { DataStatusLineItems } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { DSCampaignLineItemStatusFilterConfig } from 'c-main/entityColumns';
import { usePaginatedEntityData } from 'c-data';
import { generatePath, useHistory } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

const CommentCell: React.FC<RenderColumnDataProps<DataStatusLineItems>> = ({ entity }) => {
    const t = useCommonTranslation();
    const [val, setVal] = useState<string>(entity.comments ?? '');
    const [dirty, setDirty] = useState(false);
    const { start, isLoading, error } = useAPIClientRequest(
        ApiClient.Entities.DataStatusLineItems.update,
    );

    useEffect(() => {
        setVal(entity.comments ?? '');
        setDirty(false);
    }, [entity.id, entity.comments]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setVal(e.target.value);
        setDirty(true);
    };

    const onSave = useCallback(async () => {
        if (!dirty || isLoading) return;
        const nextComments = val.trim() === '' ? '' : val;
        await start(entity.id, { comments: nextComments });
        setDirty(false);
    }, [dirty, isLoading, start, entity.id, val]);

    return (
        <Stack alignItems="flex-start" spacing={1} sx={{ minWidth: 260 }}>
            <TextField
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={val}
                onChange={onChange}
                onBlur={onSave}
                placeholder={t(`Modules.Main.Campaigns.DataStatusLineItems.addComment`)}
                onKeyDown={e => {
                    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
                        e.preventDefault();
                        onSave();
                    }
                }}
            />
            <Stack direction="row" spacing={1}>
                {entity.updated_by_user_at}
            </Stack>
            {isLoading && <CircularProgress size={18} />}
            {error && (
                <Box sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>{String(error)}</Box>
            )}
        </Stack>
    );
};

const StatusCell: React.FC<RenderColumnDataProps<DataStatusLineItems>> = ({ entity }) => {
    const t = useCommonTranslation();
    const initial = useMemo(() => (entity.status === 0 ? 0 : 1), [entity.status]);
    const [value, setValue] = useState<number>(initial);
    const { start, isLoading, error } = useAPIClientRequest(
        ApiClient.Entities.DataStatusLineItems.update,
    );

    useEffect(() => {
        setValue(initial);
    }, [initial, entity.id]);

    const handleChange = useCallback(
        async (e: any) => {
            const next = Number(e.target.value);
            setValue(next);
            await start(entity.id, { status: next });
        },
        [entity.id, start],
    );

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 200 }}>
            <Select
                size="small"
                value={value}
                onChange={handleChange}
                disabled={isLoading}
                fullWidth
            >
                <MenuItem value={0}>
                    {t(`Modules.Main.Campaigns.DataStatusLineItems.statusUnderInvestigation`)}
                </MenuItem>
                <MenuItem value={1}>
                    {t(`Modules.Main.Campaigns.DataStatusLineItems.statusCompleted`)}
                </MenuItem>
            </Select>
            {isLoading && <CircularProgress size={18} />}
            {error && (
                <Tooltip title={String(error)}>
                    <span style={{ color: '#d32f2f', fontSize: 12 }}>!</span>
                </Tooltip>
            )}
        </Stack>
    );
};

const isTodayWithinRange = (start?: string, end?: string) => {
    if (!start || !end) return false;
    const now = new Date();
    const s = new Date(start);
    const e = new Date(end);
    const eInclusive = new Date(e);
    eInclusive.setHours(23, 59, 59, 999);
    return now >= s && now <= eInclusive;
};

const formatLocalDate = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : '');

const liveDatesCol = generateColumn<DataStatusLineItems>({
    headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.liveDates`,
    field: 'campaign.dates',
    orderable: true,
    entityName: 'DataStatusLineItems',
    RenderCell: ({ entity }) => {
        const campaign = entity.campaign;
        if (!campaign?.date_start && !campaign?.date_end) {
            return <Box sx={{ color: 'text.disabled' }}>—</Box>;
        }
        const start = formatLocalDate(campaign?.date_start);
        const end = formatLocalDate(campaign?.date_end);
        const live = isTodayWithinRange(campaign?.date_start, campaign?.date_end);
        const color = live ? 'success.main' : 'error.main';
        return (
            <Box sx={{ color, fontWeight: 600 }}>
                {start} {start && end ? '–' : ''} {end}
            </Box>
        );
    },
});

const makePercentCell = (field: 'impacts_percentage' | 'plays_percentage') =>
    function PercentCell({ entity }: RenderColumnDataProps<DataStatusLineItems>) {
        const raw = entity[field];
        if (raw == null) return null;
        const pct = raw * 100;
        const rounded = Math.round(pct);
        const color = rounded >= 100 ? 'success.main' : 'error.main';
        return <Box sx={{ color, textAlign: 'right', minWidth: 60 }}>{rounded}%</Box>;
    };

const ImpactsPercentCell = makePercentCell('impacts_percentage');
const PlaysPercentCell = makePercentCell('plays_percentage');

const cols = [
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.campaignId`,
        field: 'campaign_id',
        orderable: true,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.campaignName`,
        field: 'campaign_name',
        orderable: true,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.buyer`,
        field: 'buyer_name',
        orderable: true,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.mediaOwner`,
        field: 'display_media_owner_name',
        orderable: true,
        entityName: 'DataStatusLineItems',
    }),
    { ...liveDatesCol },
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.lineItemName`,
        field: 'display_line_item_name',
        orderable: true,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.impacts`,
        field: 'impacts_percentage',
        orderable: true,
        entityName: 'DataStatusLineItems',
        RenderCell: ImpactsPercentCell,
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.plays`,
        field: 'plays_percentage',
        orderable: true,
        RenderCell: PlaysPercentCell,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.status`,
        field: 'status',
        orderable: true,
        entityName: 'DataStatusLineItems',
        RenderCell: StatusCell,
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.comments`,
        field: 'comments',
        orderable: false,
        entityName: 'DataStatusLineItems',
        RenderCell: CommentCell,
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: `Modules.Main.Campaigns.DataStatusLineItems.lastSynced`,
        field: 'updated_at',
        orderable: false,
        entityName: 'DataStatusLineItems',
    }),
    generateColumn<DataStatusLineItems>({
        headerTitle: ``,
        field: '',
        RenderCell: ({ entity }) => {
            const history = useHistory();
            const id = entity.campaign_id;

            const lineItemName = entity.display_line_item_name ?? '';
            const safeName = encodeURIComponent(lineItemName);
            const route = `${generatePath(PostAuthRoutes.Campaigns.CampaignReporting, {
                id,
            })}?state=~(type~%27digital~metric~%27plays~pca~(metric~(filters~(scheduled~(~%27IN~%27OUT~%27UNBOOKED)~display_line_item_name~(~%27${safeName}))~countFields~(~))))`;
            return (
                <IconButton
                    component="a"
                    href={route}
                    onMouseDown={e => {
                        e.stopPropagation();
                    }}
                    onContextMenu={e => {
                        e.stopPropagation();
                    }}
                    onClick={e => {
                        e.stopPropagation();

                        if (e.metaKey || e.ctrlKey || e.button === 1) return;

                        e.preventDefault();
                        history.push(route);
                    }}
                >
                    <EditIcon />
                </IconButton>
            );
        },
        orderable: false,
        entityName: 'DataStatusLineItems',
    }),
];

const STATUS_UI_FILTER: FilterConfigSelect = {
    key: 'filter.status',
    type: CustomFilterConfigType.Select,
    label: `Modules.Main.Campaigns.DataStatusLineItems.status`,
    options: [
        { label: `Modules.Main.Campaigns.DataStatusLineItems.statusUnderInvestigation`, value: 0 },
        { label: `Modules.Main.Campaigns.DataStatusLineItems.statusCompleted`, value: 1 },
    ],
    insertAtIndex: 5,
};

const onlyIncludeFilterKeys = [
    'filter.campaign_id',
    'filter.campaign_name',
    'filter.buyer_name',
    'filter.display_media_owner_name',
    'filter.display_line_item_name',
    'filter.campaign.market_id',
];

const paginationTag = 'data-status-line-items-page';

const textSearchCols = [
    'campaign_name',
    'buyer_name',
    'display_media_owner_name',
    'display_line_item_name',
    'campaign_id',
];

const DataStatusLineItemsPage = () => {
    const t = useCommonTranslation();

    const [impactsUnderOne, setImpactsUnderOne] = useState(true);
    const [playsUnderOne, setPlaysUnderOne] = useState(false);
    const [expectedPlaysOverZero, setexpectedPlaysOverZero] = useState(true);
    const tableRef = useRef<FilterableEntityTableRefAPI>(null);
    const hasMountedRef = useRef(false);

    const updateSearchPayload = useCallback<FilterableEntityTableProps['updateSearchPayload']>(
        payload => {
            const extraFilters: Record<string, any> = {};
            extraFilters['filter.impacts_percentage'] = impactsUnderOne ? ['<1'] : [];
            extraFilters['filter.plays_percentage'] = playsUnderOne ? ['<1'] : [];
            extraFilters['filter.expected_plays'] = expectedPlaysOverZero ? ['>0'] : [];

            return {
                ...payload,
                filters: {
                    ...payload.filters,
                    ...extraFilters,
                },
            };
        },
        [impactsUnderOne, playsUnderOne, expectedPlaysOverZero],
    );

    const customFilters = useMemo(
        () => [
            <Stack direction="row" key="under-100-toggles" spacing={1}>
                <Stack alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                        {t(`Modules.Main.Campaigns.DataStatusLineItems.impacts`)}
                    </Typography>
                    <Switch checked={impactsUnderOne} onChange={(_, v) => setImpactsUnderOne(v)} />
                </Stack>
                <Stack alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                        {t(`Modules.Main.Campaigns.DataStatusLineItems.plays`)}
                    </Typography>
                    <Switch checked={playsUnderOne} onChange={(_, v) => setPlaysUnderOne(v)} />
                </Stack>
                <Stack alignItems="center">
                    <Typography variant="body2" color="textSecondary">
                        {t(`Modules.Main.Campaigns.DataStatusLineItems.expectedPlays`)}
                    </Typography>
                    <Switch
                        checked={expectedPlaysOverZero}
                        onChange={(_, v) => setexpectedPlaysOverZero(v)}
                    />
                </Stack>
            </Stack>,
        ],
        [impactsUnderOne, playsUnderOne, t, expectedPlaysOverZero],
    );

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            return;
        }

        tableRef.current?.search(1);
    }, [impactsUnderOne, playsUnderOne, expectedPlaysOverZero]);

    const { existingFilters } = usePaginatedEntityData(paginationTag, 'DataStatusLineItems');
    const talonBuyerNames = useMemo(() => {
        const cleanExistingFilters = existingFilters ?? {};
        if (Object.keys(cleanExistingFilters).length === 0) {
            return [];
        }
        const campaignNameFilter = (cleanExistingFilters as any)?.find(
            (f: any) => f?.key === 'filter.buyer_name',
        );

        if (!campaignNameFilter || !Array.isArray(campaignNameFilter.options)) {
            return [];
        }

        return campaignNameFilter.options
            .filter(opt => {
                const label = String(opt.name ?? opt.value ?? '').toLowerCase();
                return label.includes('talon');
            })
            .map(opt => opt.value);
    }, [existingFilters]);

    return (
        <Box>
            <CardHeader title={t(`Modules.Main.Campaigns.DataStatusLineItems.metaTitle`)} />
            <CardContent>
                <FilterableEntityTable
                    showFilters
                    textSearchColumns={textSearchCols}
                    resetOnUnmount={false}
                    baseEntityName="DataStatusLineItems"
                    columns={cols}
                    tag={paginationTag}
                    key={paginationTag}
                    direction="desc"
                    customFilterConfig={[STATUS_UI_FILTER, DSCampaignLineItemStatusFilterConfig]}
                    customFilters={[customFilters]}
                    updateSearchPayload={updateSearchPayload}
                    onlyIncludeFilterKeys={onlyIncludeFilterKeys}
                    includes={['campaign']}
                    ref={tableRef}
                    filters={{
                        'filter.status': [0],
                        'filter.campaign.market_id': [1],
                        'filter.buyer_name': talonBuyerNames,
                    }}
                />
            </CardContent>
        </Box>
    );
};

const setup: PageConfig = {
    id: 'DataStatusLineItemsPage',
    component: DataStatusLineItemsPage,
    systemSearch: {
        title: undefined,
        description: undefined,
        icon: dataStatusLineItemsIcon,
        type: 'page',
        aliases: undefined,
        route: PostAuthRoutes.admin.DataStatusLineItems,
        saveAsRecentPage: true,
        breadcrumbs: [],
        permissions: [PermissionName.Admin],
    },
};

export default setup;
